import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from "react-select";
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Button } from '@app/components';
import { getAllActiveFranchise } from '@app/services/franchise';
import { getCentresByFranchises } from '@app/services/centre';
import { getUserMentorCentres, updateUserMentorCentres } from '@app/services/user';

const AssignMentorCentresModal = (props: any) => {

    const { show, handleClose, user, onSuccess } = props;

    const [franchises, setFranchises] = useState<any[]>([]);
    const [centres, setCentres] = useState<any[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [isFetching, setFetching] = useState(false);

    const { values, handleSubmit, touched, errors, setFieldValue, resetForm } = useFormik({
        enableReinitialize: true,
        initialValues: {
            franchise_ids: [] as number[],
            centre_ids: [] as number[],
        },
        validationSchema: Yup.object({
            franchise_ids: Yup.array().min(1, 'Please select at least one franchise'),
            centre_ids: Yup.array().min(1, 'Please select at least one centre'),
        }),
        onSubmit: async (vals) => {
            try {
                setLoading(true);
                const resp = await updateUserMentorCentres(user.id, { centre_ids: vals.centre_ids });
                if (resp?.success === true) {
                    toast.success(resp?.message || 'Mentor centres updated successfully');
                    resetForm();
                    handleClose();
                    if (onSuccess) onSuccess(resp);
                } else {
                    toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed');
                }
            } catch (error: any) {
                toast.error(error?.message || 'Internal server error');
            } finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        if (show && user?.id) {
            fetchFranchises();
            loadMentorCentres();
        }
        if (!show) {
            resetForm();
            setCentres([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, user?.id]);

    const fetchFranchises = async () => {
        try {
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) setFranchises(resp.collections || []);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load franchises');
        }
    };

    const loadMentorCentres = async () => {
        try {
            setFetching(true);
            const resp = await getUserMentorCentres(user.id);
            if (resp?.success === true) {
                const franchiseIds = resp?.collections?.franchise_ids || [];
                const mentorCentres = resp?.collections?.centres || [];
                setFieldValue('franchise_ids', franchiseIds);
                setFieldValue('centre_ids', mentorCentres.map((c: any) => c.centre_id));
                if (franchiseIds.length > 0) {
                    await fetchCentresForFranchises(franchiseIds);
                }
            } else {
                toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed to load mentor centres');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        } finally {
            setFetching(false);
        }
    };

    const fetchCentresForFranchises = async (franchiseIds: number[]) => {
        if (!franchiseIds.length) { setCentres([]); return []; }
        try {
            const resp = await getCentresByFranchises(franchiseIds);
            if (resp?.success === true) {
                const list = resp.collections || [];
                setCentres(list);
                return list;
            } else {
                toast.error(resp?.errors?.[0]?.msg || resp?.message || 'Failed to load centres');
                return [];
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
            return [];
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Assign Centers — Mentor Role — {user?.first_name} {user?.last_name}</Modal.Title>
            </Modal.Header>
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="form-group">
                                <label>Franchise(s) <span className="required">*</span></label>
                                <Select
                                    isMulti
                                    isDisabled={isFetching}
                                    placeholder="Select franchise(s)"
                                    options={franchises.map((f: any) => ({ value: f.id, label: f.name }))}
                                    onChange={async (opts: any) => {
                                        const ids = opts ? opts.map((o: any) => o.value) : [];
                                        setFieldValue('franchise_ids', ids);
                                        const updatedCentres = await fetchCentresForFranchises(ids);
                                        // Drop any previously selected centres that are no longer valid
                                        // options for the newly selected franchise(s).
                                        const validIds = (updatedCentres || []).map((c: any) => c.id);
                                        setFieldValue('centre_ids', values.centre_ids.filter((cid: number) => validIds.includes(cid)));
                                    }}
                                    value={franchises.filter((f: any) => values.franchise_ids.includes(f.id)).map((f: any) => ({ value: f.id, label: f.name }))}
                                    closeMenuOnSelect={false}
                                />
                                {touched.franchise_ids && errors.franchise_ids && <div className="text-danger">{errors.franchise_ids as string}</div>}
                            </div>
                        </div>
                        <div className="col-sm-12">
                            <div className="form-group">
                                <label>Assign Centre(s) <span className="required">*</span></label>
                                <Select
                                    isMulti
                                    isDisabled={isFetching}
                                    placeholder="Select centre(s)"
                                    options={centres.map((c: any) => ({ value: c.id, label: c.name }))}
                                    onChange={(opts: any) => setFieldValue('centre_ids', opts ? opts.map((o: any) => o.value) : [])}
                                    value={centres.filter((c: any) => values.centre_ids.includes(c.id)).map((c: any) => ({ value: c.id, label: c.name }))}
                                    closeMenuOnSelect={false}
                                />
                                {touched.centre_ids && errors.centre_ids && <div className="text-danger">{errors.centre_ids as string}</div>}
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" isLoading={isLoading} className="btn btn-primary mr-2">Save</Button>
                    <Button className="btn btn-default" onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default AssignMentorCentresModal;
